import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate } from 'react-router-dom';
import { AdminProductDTO } from '@src/common-utils/DataModels';
import { isAuthError } from '@src/common-utils/AuthModels';
import { Nullable } from '@src/common-utils/Models';
import { ProductDialog, ProductDialogProps } from '@src/components/product-management/ProductDialog';
import { ProductManagementGrid } from '@src/components/product-management/ProductManagementGrid';
import { useNotificationContext } from '@src/contexts/NotificationProvider';
import { useSearchContext } from '@src/contexts/SearchContextProvider';
import { useUserContext } from '@src/contexts/UserContextProvider';
import { ROUTES } from '@src/layout/RoutePage';
import { useAdminProductsInfiniteQuery, useDeleteProduct } from '@src/queries/ProductManagementQuery';

/** SystemAdmin or ProductAdmin only (see the route guard in RoutePage.tsx; the API enforces it as well). */
export const ProductManagement = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const confirm = useConfirm();

  const { logout } = useUserContext();
  const { showMessage } = useNotificationContext();
  const { productSearchName, setIsLoading } = useSearchContext();

  const [dialogMode, setDialogMode] = useState<ProductDialogProps['mode']>('CREATE');
  const [selectedProduct, setSelectedProduct] = useState<Nullable<AdminProductDTO>>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const notify = useCallback(
    (message: string, isSuccess = true) =>
      showMessage(message, {
        position: isSmall ? 'top-center' : undefined,
        direction: isSmall ? 'down' : undefined,
        variant: isSuccess ? 'success' : 'error',
      }),
    [showMessage, isSmall],
  );

  const handleError = useCallback(
    (error: Error) => {
      if (isAuthError(error)) {
        logout().finally(() => navigate(ROUTES.signin));
      } else {
        notify(error.message, false);
      }
    },
    [logout, navigate, notify],
  );

  const { products, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useAdminProductsInfiniteQuery({ searchName: productSearchName, onError: handleError });

  const { deleteProduct } = useDeleteProduct();

  // drive the spinner of the header search box
  useEffect(() => {
    setIsLoading(isLoading || isFetching);
    return () => setIsLoading(false);
  }, [isLoading, isFetching, setIsLoading]);

  const handleAdd = () => {
    setSelectedProduct(null);
    setDialogMode('CREATE');
    setDialogOpen(true);
  };

  const handleEdit = (product: AdminProductDTO) => {
    setSelectedProduct(product);
    setDialogMode('UPDATE');
    setDialogOpen(true);
  };

  const handleDelete = (product: AdminProductDTO) => {
    confirm({
      title: 'Delete product',
      description: `Permanently delete ${product.code} - ${product.name}? Its uploaded images are removed too. This cannot be undone.`,
      confirmationText: 'Delete',
    })
      .then(async () => {
        try {
          await deleteProduct(product.code);
          notify(`Product ${product.code} was deleted`);
        } catch (e) {
          handleError(e instanceof Error ? e : new Error('Failed to delete the product'));
        }
      })
      .catch(() => {
        // cancelled
      });
  };

  const handleSaved = (product: AdminProductDTO) => {
    setDialogOpen(false);
    notify(`Product ${product.code} was ${dialogMode === 'CREATE' ? 'added' : 'updated'} successfully`);
  };

  return (
    <>
      <ProductManagementGrid
        data={products}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      {dialogOpen && (
        <ProductDialog
          open={dialogOpen}
          mode={dialogMode}
          product={selectedProduct}
          onClose={() => setDialogOpen(false)}
          onSuccess={handleSaved}
        />
      )}
    </>
  );
};
